import React from "react"
import {
  Box,
  Dialog,
  IconButton,
  List,
  ListItemButton,
  Slide,
  useTheme,
} from "@mui/material"
import clsx from "clsx"
import _ from "lodash"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { getFeatureDetail } from "utils/feature"
import { getIcon } from "utils/icon"
import { CloseIcon } from "components/icons"
import { Image } from "components/Image"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import { TransitionProps } from "@mui/material/transitions"
import { LeftToRightWrapper } from "components/LeftToRightWrapper"
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export const OrdinalSelector = ({
  selectedOrdinal,
  levels,
  onClickOrdinal,
  currentVenue,
  viewingVenueId,
  locationOrdinal,
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const {
    root,
    floorAvatar,
    iconWrapper,
    dialog,
    wrapper,
    closeButton,
    logoImage,
    logoWrapper,
    levelText,
    ordinalContainer,
    venueOrdinals,
    logoContainer,
  } = theme.components[`OrdinalSelector`]?.styleOverrides || {}
  const { t } = useTranslation()
  const { venues = [] } = useVenue()
  const handleOpen = useCallback(() => {
    setOpen(true)
  }, [])
  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const arrowIcon = useMemo(
    () => getIcon(theme?.assets?.arrowUpIcon) || <KeyboardArrowUpIcon />,
    [theme]
  )
  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.ordinalSelector?.closeIcon) || <CloseIcon />,
    [theme]
  )

  const currentLevel = useMemo(
    () =>
      levels.find(({ properties }) => selectedOrdinal === properties.ordinal),
    [levels, selectedOrdinal]
  )

  const venueInfos = useMemo(() => {
    return venues.map(getFeatureDetail)
  }, [venues])

  const ordinalsByVenue = {}
  venueInfos.forEach((venue) => {
    const venueLevels = levels.filter(
      (level) => level.properties.venue.properties.reference === venue.reference
    )

    ordinalsByVenue[venue.reference] = {}

    venueLevels.forEach((level) => {
      const ordinal = level.properties?.ordinal
      ordinalsByVenue[venue.reference][ordinal] = level
    })
  })
  const allOrdinals = useMemo(
    () => _.uniq(levels.map((level) => level.properties?.ordinal)),
    [levels]
  )

  const handleClick = useCallback(
    (ordinal, id) => () => {
      onClickOrdinal(ordinal, id)
      setOpen(false)
    },
    [onClickOrdinal]
  )

  return (
    <>
      <Box onClick={handleOpen} sx={root}>
        <Box sx={iconWrapper}>{arrowIcon}</Box>
        <Box sx={floorAvatar}>{currentLevel.properties.name?.en}</Box>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        title={t("Select Floor")}
        sx={dialog}
        TransitionComponent={Transition}
      >
        <Box sx={wrapper}>
          <IconButton onClick={handleClose} sx={closeButton}>
            {closeIcon}
          </IconButton>
          <LeftToRightWrapper>
            <Box sx={logoContainer}>
              {venueInfos.map(({ id, name, logo, reference }) => {
                return (
                  <Box sx={logoWrapper} key={id} className={reference}>
                    {logo ? <Image src={logo} sx={logoImage} /> : t(name)}
                  </Box>
                )
              })}
            </Box>
            <Box sx={ordinalContainer}>
              {venueInfos.map(({ reference, id }) => {
                const levelsInVenue = ordinalsByVenue[reference]
                return (
                  <List
                    className={reference}
                    key={`ordinal-selector-${reference}`}
                    sx={venueOrdinals}
                  >
                    {allOrdinals.map((ordinal) => {
                      const level = levelsInVenue[ordinal]
                      const isCurrentLocation =
                        currentVenue === id && locationOrdinal === ordinal
                      const isActiveOrdinal =
                        viewingVenueId === id && selectedOrdinal === ordinal
                      return (
                        <ListItemButton
                          sx={{
                            ...levelText,
                            pointerEvents: level?.properties?.name?.en
                              ? "auto"
                              : "none",
                          }}
                          key={`${reference}-ordinal-${ordinal}`}
                          className={clsx(
                            reference,
                            `ordinal-${ordinal}`,
                            isCurrentLocation ? "current-location" : ""
                          )}
                          onClick={handleClick(ordinal, id)}
                          selected={isActiveOrdinal}
                        >
                          {level?.properties?.name?.en}
                        </ListItemButton>
                      )
                    })}
                  </List>
                )
              })}
            </Box>
          </LeftToRightWrapper>
        </Box>
      </Dialog>
    </>
  )
}
